/**
 * ImpersonationBanner — persistent header ribbon shown across the employer
 * surface (Store · Shortlist · Discover · Résumé detail) when an admin has
 * started a "View as <employer>" session via the /admin/employers page.
 *
 * Props:
 *   · employer   — the `useEmployer()` object; the banner reads
 *                  `impersonation_expires_at` + `company` from it
 *   · onExit     — callback fired after the stop request succeeds so the
 *                  parent can force-refresh /api/employer/me + re-render
 *
 * Visual rules:
 *   · Red-tinged mustard bar with lock icon — stands out against midnight bg
 *   · Countdown to expiry (mm:ss) auto-updates every second
 *   · At <60s left, header pulses so admin knows to renew or exit cleanly
 *   · Clicking Exit calls /api/admin/employers/stop-impersonating + reloads
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, LogOut, Timer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0F1B2D";
const MUSTARD = "#FFC145";

function formatCountdown(secondsLeft) {
  if (secondsLeft <= 0) return "expired";
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export default function ImpersonationBanner({ employer, onExit }) {
  const expiresAt = employer?.impersonation_expires_at;
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [busy, setBusy] = useState(false);

  // Tick every second so the countdown updates. We stop ticking once the
  // session's expired — the "expired" state is terminal.
  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const stop = useCallback(async () => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/employers/stop-impersonating`,
        {}, { withCredentials: true });
    } catch { /* still call onExit — a stale cookie should be clearable */ }
    setBusy(false);
    if (onExit) onExit();
  }, [onExit]);

  if (!employer?.is_admin_impersonating) return null;

  const secondsLeft = expiresAt ? Math.max(0, expiresAt - now) : 0;
  const expired = secondsLeft <= 0;
  const isCritical = !expired && secondsLeft < 60;

  return (
    <div
      className="w-full"
      style={{
        background: expired ? "#7F1D1D"    // red on expiry
                  : isCritical ? "#FDBA74" // amber pulse near expiry
                  : MUSTARD,               // mustard steady state
        color: expired ? "white" : NAVY,
        borderBottom: `2px solid ${NAVY}`,
        boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
        animation: isCritical && !expired ? "impersonate-pulse 1.2s ease-in-out infinite" : "none",
      }}
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center gap-3 flex-wrap">
        <AlertTriangle size={16} style={{ color: expired ? "white" : NAVY }} />
        <div className="font-mono-rm text-[11px] uppercase tracking-widest" style={{ fontWeight: 800 }}>
          · Admin View As
        </div>
        <div className="text-[13px] font-medium" style={{ fontWeight: 700 }}>
          You are viewing as{" "}
          <span className="font-display" style={{ letterSpacing: "-0.005em" }}>
            {employer.company || employer.email}
          </span>
          {" "}· <span style={{ opacity: 0.85 }}>read-only</span>
        </div>
        <div
          className="inline-flex items-center gap-1 text-[11px] font-mono-rm uppercase tracking-widest px-2 py-0.5 rounded"
          style={{
            background: expired ? "rgba(255,255,255,0.15)" : "rgba(15,27,45,0.14)",
            color: expired ? "white" : NAVY,
            fontWeight: 800,
          }}
          data-testid="impersonation-countdown"
        >
          <Timer size={11} />
          {expired ? "session expired" : `${formatCountdown(secondsLeft)} left`}
        </div>
        <button
          type="button"
          onClick={stop}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1 rounded px-3 py-1 text-[11px] font-mono-rm uppercase tracking-widest"
          style={{
            background: NAVY, color: MUSTARD,
            border: `1.5px solid ${NAVY}`,
            fontWeight: 800,
            cursor: busy ? "wait" : "pointer",
          }}
          data-testid="impersonation-exit"
        >
          <LogOut size={11} />
          {expired ? "Clear session" : "Exit impersonation"}
        </button>
      </div>
      <style>{`
        @keyframes impersonate-pulse {
          0%, 100% { filter: brightness(1); }
          50%      { filter: brightness(1.18); }
        }
      `}</style>
    </div>
  );
}
